import { renderStructuredText, stripHtmlTags } from "lib/prismic";
import { SliceZone } from "@prismicio/react";
import { useState } from "react";
import { ImageViewer } from "components/ImageViewer";

function InstructionImages({
  images,
  altPrefix,
  imagesPerRow,
}: {
  images: any[];
  altPrefix: string;
  imagesPerRow?: string | null;
}) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  if (images.length === 0) return null;
  const columns =
    imagesPerRow === "1" || imagesPerRow === "2" || imagesPerRow === "3"
      ? Number(imagesPerRow)
      : Math.min(images.length, 3);
  const columnClass =
    columns === 1
      ? "grid-cols-1"
      : columns === 2
      ? "grid-cols-2"
      : "grid-cols-3";
  const viewerImages = images.map((image, index) => ({
    url: image.url,
    alt: image.alt || `${altPrefix} - Image ${index + 1}`,
  }));
  return (
    <>
      <div className={`grid ${columnClass} gap-4 max-w-[1070px] mx-auto`}>
        {viewerImages.map((image, index) => (
          <button
            key={index}
            type="button"
            aria-label={`Enlarge ${image.alt}`}
            onClick={() => setSelectedIndex(index)}
            className="min-w-0 cursor-zoom-in rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-3461fd"
          >
            <img
              src={image.url}
              alt={image.alt}
              className="w-full h-auto aspect-square object-contain rounded-lg"
            />
          </button>
        ))}
      </div>
      {selectedIndex !== null && (
        <ImageViewer
          images={viewerImages}
          initialIndex={selectedIndex}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </>
  );
}

function InstructionsToggle() {
  return (
    <summary
      aria-label="Toggle task instructions"
      className="absolute right-4 top-4 flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-lg text-858494 hover:bg-f4f7ff hover:text-191c20 [&::-webkit-details-marker]:hidden"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="h-5 w-5 transition-transform group-open:rotate-180"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </summary>
  );
}

export function PhoneInstructions({
  slices,
  taskTitle,
  stepTitle,
  numberOfCaptures,
  hideInstructions,
}: {
  slices: any[];
  taskTitle?: any;
  stepTitle: string;
  numberOfCaptures: number;
  hideInstructions: boolean;
}) {
  return (
    <div className="relative rounded-xl border border-f4f7ff p-4 mb-8">
      <div className="px-8 text-center">
        {taskTitle && (
          <div
            className="text-2xl font-bold"
            dangerouslySetInnerHTML={{
              __html: renderStructuredText(taskTitle),
            }}
          />
        )}
        <h3
          className={`text-lg font-medium text-3461fd${
            taskTitle ? " mt-2" : ""
          }`}
        >
          {stepTitle || "Instructions"}
        </h3>
        {numberOfCaptures > 1 && (
          <p className="mt-1 text-sm text-3461fd">
            {numberOfCaptures} captures required
          </p>
        )}
      </div>
      <details open={!hideInstructions} className="group open:mt-6">
        <InstructionsToggle />
        <div className="space-y-6">
          <SliceZone
            slices={slices}
            components={taskInstructionSliceComponents}
            context={{ inlineInstructions: true }}
          />
        </div>
      </details>
    </div>
  );
}

const getGoogleDriveEmbed = (embedData: any): string | null => {
  if (!embedData) return null;

  const embedUrl = embedData.embed_url || embedData.url || "";
  const googleDriveMatch = embedUrl.match(
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/
  );

  if (googleDriveMatch) {
    const fileId = googleDriveMatch[1];
    return `<iframe src="https://drive.google.com/file/d/${fileId}/preview" width="100%" height="480" allow="autoplay" style="max-width: 100%; border: none;"></iframe>`;
  }

  return embedData.html || null;
};

export const taskInstructionSliceComponents = {
  instruction_text___image: ({ slice, context, index }: any) => {
    const ContentContainer = context?.inlineInstructions ? "div" : "details";
    return (
      <div
        className={
          context?.inlineInstructions
            ? "space-y-6"
            : "relative rounded-xl border border-f4f7ff p-4 mb-8"
        }
      >
        {!context?.inlineInstructions &&
          (slice.primary?.title1 || (index === 0 && context?.orbStepLabel)) && (
            <div className="text-center px-8">
              {slice.primary?.title1 && (
                <div
                  className="text-2xl font-bold"
                  dangerouslySetInnerHTML={{
                    __html: renderStructuredText(slice.primary.title1),
                  }}
                />
              )}
              {index === 0 && context?.orbStepLabel && (
                <h2 className="text-lg font-medium text-3461fd mt-2">
                  {context.orbStepLabel}
                </h2>
              )}
            </div>
          )}

        {!context?.inlineInstructions &&
          !slice.primary?.title1 &&
          !(index === 0 && context?.orbStepLabel) &&
          (slice.primary?.task_level_video || slice.items?.length > 0) && (
            <p className="text-center font-medium px-8">Instructions</p>
          )}

        {(slice.primary?.task_level_video || slice.items?.length > 0) && (
          <ContentContainer
            {...(!context?.inlineInstructions
              ? { open: !slice.primary?.hide_instructions }
              : {})}
            className={context?.inlineInstructions ? "" : "group open:mt-6"}
          >
            {!context?.inlineInstructions && <InstructionsToggle />}
            <div className="space-y-6">
              {slice.primary?.task_level_video && (
                <div className="flex justify-center mb-6">
                  <div
                    className="max-w-full w-full"
                    dangerouslySetInnerHTML={{
                      __html:
                        getGoogleDriveEmbed(slice.primary.task_level_video) ||
                        "",
                    }}
                  />
                </div>
              )}

              {slice.items && slice.items.length > 0 && (
                <div className="space-y-6">
                  {slice.items.map((item: any, itemIndex: number) => {
                    const itemImages = [
                      item.image_1,
                      item.image_2,
                      item.image_3,
                      item.image_4,
                      item.image_5,
                    ].filter((img) => img?.url);

                    return (
                      <div key={itemIndex} className="space-y-4">
                        <InstructionImages
                          images={itemImages}
                          altPrefix={`Instruction ${itemIndex + 1}`}
                          imagesPerRow={item.images_per_row}
                        />

                        {item.step_description && (
                          <div className="prose prose-sm max-w-none text-191c20 leading-relaxed mx-auto px-4">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: renderStructuredText(
                                  item.step_description
                                ),
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </ContentContainer>
        )}
      </div>
    );
  },

  step_instructions_: ({ slice, context }: any) => {
    const primary = slice.primary;
    if (!primary) return null;

    // Collect all images from the primary fields
    const images = [primary.image_1, primary.image_2, primary.image_3].filter(
      (img) => img?.url
    );

    return (
      <div className="space-y-6 p-6 bg-gray-50 rounded-xl">
        {!context?.inlineInstructions && (
          <div className="flex items-center gap-4">
            {primary.step_number && (
              <div className="flex-shrink-0 w-8 h-8 bg-3461fd text-white rounded-full flex items-center justify-center text-sm font-semibold">
                {primary.step_number}
              </div>
            )}

            <div className="flex-1">
              {primary.step_title && (
                <h3 className="text-lg font-semibold text-191c20">
                  {stripHtmlTags(renderStructuredText(primary.step_title))}
                </h3>
              )}
              {context?.phoneStepLabel && (
                <p className="text-sm font-medium text-3461fd mt-1">
                  {context.phoneStepLabel}
                </p>
              )}

              {Number(primary.number_of_captures) > 1 && (
                <div className="text-sm text-3461fd font-medium mt-1">
                  {primary.number_of_captures} captures required
                </div>
              )}
            </div>
          </div>
        )}

        {primary.step_description && (
          <div className="prose prose-sm max-w-none text-191c20 leading-relaxed mx-auto px-4">
            <div
              dangerouslySetInnerHTML={{
                __html: renderStructuredText(primary.step_description),
              }}
            />
          </div>
        )}

        <InstructionImages
          images={images}
          altPrefix={`Step ${primary.step_number || ""}`}
          imagesPerRow={primary.images_per_row}
        />
      </div>
    );
  },

  step_details: ({ slice }: any) => {
    return null;
  },
};
